import { createContext, useEffect, useReducer } from 'react';

import { PrimaryType } from '@/meta';
import { assertUnreachable } from '@/utils';

export interface State {
  /** The selected scopes in a query */
  scopes: PrimaryType[];

  /** The values associated with scopes */
  scopeValues: Record<PrimaryType, FieldOption<string>[]>;

  /** Remaining valid options */
  options: PrimaryType[];
}

type PrimaryTypeQuery = 'capabilityPublicIds' | 'teamPublicIds' | 'productPublicIds';

export const scopeToQuery: Record<PrimaryType, PrimaryTypeQuery> = {
  capability: 'capabilityPublicIds',
  team: 'teamPublicIds',
  product: 'productPublicIds',
};

export const queryToScope: Record<PrimaryTypeQuery, PrimaryType> = {
  capabilityPublicIds: 'capability',
  teamPublicIds: 'team',
  productPublicIds: 'product',
};

type onChangeHandler = (scope: PrimaryType, index: number, values?: FieldOption<string>[]) => void;

export interface Reducer {
  state: State;
  onAdd: () => void;
  onChange: onChangeHandler;
  onRemove: (scope: PrimaryType) => void;
  onSet: (newState: State) => void;
}

type ScopePayload = {
  index?: number;
  scope: PrimaryType;
  values?: FieldOption<string>[];
};

type DispatchAction =
  | {
      type: 'add';
    }
  | {
      type: 'remove';
      data: ScopePayload;
    }
  | {
      type: 'edit';
      data: ScopePayload;
    }
  | {
      type: 'change';
      data: ScopePayload;
    }
  | {
      type: 'refresh';
      newState: State;
    };

export const initialState: State = {
  scopes: ['team'],
  options: ['product', 'capability'],
  scopeValues: {
    team: [],
    product: [],
    capability: [],
  },
};

/**
 * Adds a value for 'scope' to the query
 *
 * @param state current slice of state
 */
function addQuery(state: State) {
  const nextValue = state.options.pop();

  return {
    scopes: state.scopes.concat(nextValue!),
    options: state.options,
    scopeValues: state.scopeValues,
  };
}

/**
 * remove the the scope specified from the current query
 */
function removeQuery(state: State, payload: ScopePayload) {
  return {
    scopes: state.scopes.filter(option => option !== payload.scope),
    options: state.options.concat(payload.scope),
    scopeValues: {
      ...state.scopeValues,
      [payload.scope]: [],
    },
  };
}

function editQuery(state: State, payload: ScopePayload) {
  const newState = {
    scopes: state.scopes,
    options: state.options,
    scopeValues: {
      ...state.scopeValues,
      [payload.scope]: payload.values || [],
    },
  };

  return newState;
}

function changeQuery(state: State, payload: ScopePayload) {
  /** else we're updating the actual scope */
  const oldValue = state.scopes[payload.index!];
  const options = state.options.filter(option => option !== payload.scope).concat(oldValue);
  const scopes = state.scopes.map((scope: PrimaryType, index: number) => {
    if (index === payload.index) {
      return payload.scope;
    }
    return scope;
  });

  return {
    scopes,
    options,
    scopeValues: state.scopeValues,
  };
}

function searchBuilderReducer(state: State, action: DispatchAction) {
  switch (action.type) {
    case 'add':
      return addQuery(state);
    case 'edit':
      return editQuery(state, action.data);
    case 'change':
      return changeQuery(state, action.data);
    case 'remove':
      return removeQuery(state, action.data);
    case 'refresh':
      return {
        ...state,
        ...action.newState,
      };
    default:
      return assertUnreachable(action);
  }
}

/**
 * State Manager for the Search builder
 */
function useSearchBuilderController() {
  /** initialisation */
  const [state, dispatch] = useReducer(searchBuilderReducer, initialState);

  useEffect(() => {
    const q = state.scopes.reduce((queryString, scope) => {
      const values = state.scopeValues[scope].map(({ value }) => value);

      if (!values.length) {
        return queryString;
      }

      const query = `${scopeToQuery[scope]}=${values.join(',')}`;
      if (!queryString) {
        return query;
      }
      return `${queryString}&${query}`;
    }, '');

    const queryString = q ? `?${q}` : '?search=*';

    /** side effect inside reducer :( */
    window.history.pushState(null, 'Reporting Dashboard', queryString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.scopeValues.team, state.scopeValues.product, state.scopeValues.capability]);

  const onAdd = () => dispatch({ type: 'add' });

  const onRemove = (scope: PrimaryType) => {
    dispatch({
      type: 'remove',
      data: {
        scope,
      },
    });
  };

  const onChangeValues = (scope: PrimaryType, values: FieldOption<string>[] = []) => {
    dispatch({
      type: 'edit',
      data: {
        scope,
        values,
      },
    });
  };

  const onChangeScope = (scope: PrimaryType, index: number) => {
    dispatch({
      type: 'change',
      data: {
        scope,
        index,
      },
    });
  };

  const onSet = (newState: State) => dispatch({ type: 'refresh', newState });

  return {
    state,
    onAdd,
    onChangeValues,
    onChangeScope,
    onRemove,
    onSet,
  };
}

/* FIXME: searchV2: deprecate */

export const SearchBuilderStateContext = createContext(initialState);
export type SearchAPI = ReturnType<typeof useSearchBuilderController>;

export default useSearchBuilderController;
